import React from "react";
import { ModalOptionLabel, ColorPaletteOption } from "./components";
import { Input, Select, Switch } from "antd";
import { palettes } from "@/components/VivoPlots/functions/drawPlotData/utils/palletes";
import type { AxisSectionProps } from "@/components/VivoPlots/types";

const AxisSection = ({
  axisName,
  showTicks,
  onShowTicksChange,
  showLabel,
  onShowLabelChange,
  labelValue,
  onLabelValueChange,
}: AxisSectionProps) => {
  const testIdAxis = axisName.toLowerCase();

  return (
    <div className="flex flex-col gap-2">
      <span className="text-[13px] font-medium text-[#2B4752]">
        {axisName}-Axis
      </span>
      <div className="flex flex-row items-center justify-between">
        <ModalOptionLabel label="Show ticks" />
        <Switch
          aria-label={`Show ${axisName}-Axis ticks`}
          data-testid={`plot-design-show-${testIdAxis}-ticks-switch`}
          checked={showTicks}
          onChange={(checked) => onShowTicksChange(checked)}
        />
      </div>
      <div className="flex flex-row items-center justify-between">
        <ModalOptionLabel label="Show label" />
        <Switch
          aria-label={`Show ${axisName}-Axis label`}
          data-testid={`plot-design-show-${testIdAxis}-axis-label-switch`}
          checked={showLabel}
          onChange={(checked) => onShowLabelChange(checked)}
        />
      </div>
      {showLabel && (
        <Input
          aria-label={`${axisName}-Axis label`}
          data-testid={`plot-design-${testIdAxis}-axis-label-input`}
          placeholder={`Enter ${axisName} axis label`}
          className="w-full plotmodal"
          value={labelValue}
          onChange={(e) => onLabelValueChange(e.target.value)}
        />
      )}
    </div>
  );
};

export const DesignTab = ({ setters, getters }) => {
  const {
    plotTitle,
    showXTicks,
    showYTicks,
    showXAxisLabel,
    showYAxisLabel,
    showSwarm,
    plotSubtitle,
    plotXAxisLabel,
    plotYAxisLabel,
    plotType,
    plotSize,
    colorPalette,
    isHorizontal,
    metricUnit,
    metricThresholdWarn,
    metricThresholdCritical,
    metricThresholdDirection,
  } = getters;
  const {
    setPlotSize,
    setColorPalette,
    setPlotTitle,
    setPlotXAxisLabel,
    setPlotYAxisLabel,
    setShowXTicks,
    setShowYTicks,
    setShowXAxisLabel,
    setShowYAxisLabel,
    setPlotSubtitle,
    setShowSwarm,
    setMetricUnit,
    setMetricThresholdWarn,
    setMetricThresholdCritical,
    setMetricThresholdDirection,
  } = setters;

  const isMetricCard = plotType === "metric-card";

  // The UI's X-Axis section = the visual bottom axis, Y-Axis = the visual left
  // axis. Label text is stored per data variable and the renderer draws the X
  // variable on the left when horizontal, so bind each section's rename input
  // to whichever label actually renders on that visual axis.
  const bottomAxisLabel = isHorizontal ? plotYAxisLabel : plotXAxisLabel;
  const setBottomAxisLabel = isHorizontal
    ? setPlotYAxisLabel
    : setPlotXAxisLabel;
  const leftAxisLabel = isHorizontal ? plotXAxisLabel : plotYAxisLabel;
  const setLeftAxisLabel = isHorizontal ? setPlotXAxisLabel : setPlotYAxisLabel;

  return (
    <div className="flex flex-col gap-4 pr-2 pb-8">
      <div className="flex flex-col gap-2">
        <ModalOptionLabel label="Plot Title" />
        <Input
          data-testid="plot-design-title-input"
          placeholder="Enter plot name"
          required
          status={plotTitle === "" ? "error" : ""}
          className="w-full plotmodal"
          value={plotTitle}
          onChange={(e) => setPlotTitle(e.target.value)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <ModalOptionLabel label="Caption" />
        <Input
          data-testid="plot-design-caption-input"
          placeholder="Enter plot subtitle"
          className="w-full plotmodal"
          value={plotSubtitle}
          onChange={(e) => setPlotSubtitle(e.target.value)}
        />
      </div>
      <div className="flex flex-col gap-2">
        <ModalOptionLabel label="Plot size" />
        <Select
          data-testid="plot-design-size-select"
          placeholder="Enter plot size"
          className="w-full plotmodal"
          options={[
            {
              value: "small",
              label: "Small",
            },
            {
              label: "Medium",
              value: "medium",
            },
            {
              label: "Large",
              value: "large",
            },
          ]}
          value={plotSize}
          onChange={(e) => setPlotSize(e)}
        />
      </div>
      <div className="">
        <ModalOptionLabel label="Color palette" />
        <Select
          data-testid="plot-design-color-palette-select"
          placeholder="Enter Color Palette"
          className="w-full mt-2 plotmodal"
          options={Object.keys(palettes).map((column, index) => ({
            key: `${column}-${index}`,
            label: <ColorPaletteOption value={column} />,
            value: column,
          }))}
          value={colorPalette}
          onChange={(e) => setColorPalette(e)}
        />
      </div>
      {plotType === "swarm-box" && (
        <div className="flex flex-row items-center justify-between">
          <ModalOptionLabel label="Show swarm on toggle" />
          <Switch
            data-testid="plot-design-show-swarm-switch"
            checked={showSwarm}
            onChange={(e) => setShowSwarm(e)}
          />
        </div>
      )}

      {!isMetricCard && (
        <div
          data-testid="plot-design-axes-zone"
          className="flex flex-col gap-3 rounded-lg border border-[#E8E8E8] bg-[#FAFBFC] p-3"
        >
          <ModalOptionLabel label="Axes" />

          <AxisSection
            axisName="X"
            showTicks={showXTicks}
            onShowTicksChange={setShowXTicks}
            showLabel={showXAxisLabel}
            onShowLabelChange={setShowXAxisLabel}
            labelValue={bottomAxisLabel}
            onLabelValueChange={setBottomAxisLabel}
          />

          <AxisSection
            axisName="Y"
            showTicks={showYTicks}
            onShowTicksChange={setShowYTicks}
            showLabel={showYAxisLabel}
            onShowLabelChange={setShowYAxisLabel}
            labelValue={leftAxisLabel}
            onLabelValueChange={setLeftAxisLabel}
          />
        </div>
      )}

      {isMetricCard && (
        <>
          <div className="flex flex-col gap-2">
            <ModalOptionLabel
              label="Unit"
              tooltip="Optional unit shown after the value (e.g. %, mg/dL)"
            />
            <Input
              data-testid="plot-design-metric-unit-input"
              placeholder="e.g. %"
              className="w-full plotmodal"
              value={metricUnit ?? ""}
              onChange={(e) => setMetricUnit?.(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-2">
            <ModalOptionLabel
              label="Threshold direction"
              tooltip="Whether high or low values trigger warnings"
            />
            <Select
              data-testid="plot-design-metric-threshold-direction-select"
              className="w-full plotmodal"
              value={metricThresholdDirection ?? "above"}
              options={[
                { label: "Warn when above threshold", value: "above" },
                { label: "Warn when below threshold", value: "below" },
              ]}
              onChange={(val) => setMetricThresholdDirection?.(val)}
            />
          </div>
          <div className="flex flex-row gap-2">
            <div className="flex flex-col gap-1 flex-1">
              <ModalOptionLabel label="Warn threshold" />
              <Input
                data-testid="plot-design-metric-warn-input"
                placeholder="e.g. 50"
                className="w-full plotmodal"
                value={metricThresholdWarn ?? ""}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  setMetricThresholdWarn?.(isNaN(n) ? undefined : n);
                }}
              />
            </div>
            <div className="flex flex-col gap-1 flex-1">
              <ModalOptionLabel label="Critical threshold" />
              <Input
                data-testid="plot-design-metric-critical-input"
                placeholder="e.g. 75"
                className="w-full plotmodal"
                value={metricThresholdCritical ?? ""}
                onChange={(e) => {
                  const n = parseFloat(e.target.value);
                  setMetricThresholdCritical?.(isNaN(n) ? undefined : n);
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
